import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { feedbackTransforms } from '@doltech/ui/lib/shared/components/IELTSample/Feedback/plugins/transforms';
import { outlineParagraphTransforms } from '@doltech/ui/lib/shared/components/IELTSample/Outline/Paragraph/transforms';
import { outlineStatementTransforms } from '@doltech/ui/lib/shared/components/IELTSample/Outline/Statement/transforms';
import { questionListTransforms } from '@doltech/ui/lib/shared/components/IELTSample/QuestionList/transforms';
import { generateVocabIdeaTitleNode } from '@doltech/ui/lib/shared/components/IELTSample/shared/plugins/VocabIdeaTitlePlugin';
import { ELEMENT_VOCAB_IDEA_TITLE } from '@doltech/ui/lib/shared/components/IELTSample/shared/plugins/VocabIdeaTitlePlugin.types';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { FeedbackTypeEnum } from '@doltech/utils/lib/enum/FeedbackTypeEnum';
import { getSlateNodeAsTextString } from '@doltech/utils/lib/slateUtils';
import { onAction } from 'mobx-state-tree';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { useInterval } from 'react-use';
import { Node } from 'slate';
import styled from 'styled-components';

import { useStepStore } from '../step-header/model/base-step';

export const SharedPanel = styled.div`
  width: 712px;
  margin: 24px auto 120px;
  display: grid;
  gap: 16px;
  .panel,
  .panel-add-paragraph {
    background-color: ${colorsV2.white100};
    padding: 24px;
    border-radius: 16px;
  }

  .panel-editor {
    .dol-editor {
      padding: 0;
      background-color: transparent;
    }
  }

  .float-action {
    position: fixed;
    left: calc(50% + ${712 / 2 + 24}px);
  }
`;

export const PopConfirmContent = styled.div`
  width: 240px;
`;

export const PanelBlock = styled.div`
  background-color: ${colorsV2.white100};
  border-radius: 16px;
  .header {
    padding: 12px 24px;
    border-bottom: 1px solid ${colorsV2.gray10};
  }
  .body {
    padding: 24px;
  }
`;

export const PreviewMain = styled.div`
  margin-top: 32px;
  background-color: ${colorsV2.white100};
  padding-bottom: 52px;

  .section-container {
    width: calc(100% - 32px);
    margin: 0 auto;

    ${fromScreen(458)} {
      width: 425px;
    }
    ${fromScreen(780)} {
      width: 712px;
    }
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
  .section-block {
    display: grid;
    gap: 24px;

    &.mobile-full {
      padding: 0;
    }
  }

  .grid-row {
    display: grid;
    gap: 24px;
  }

  .hidden {
    display: none;
  }

  #left-interaction-sidebar {
    width: 160px;
    min-width: 160px;
    max-width: 160px;
  }

  @media ${deviceBreakpoints.fromTablet} {
    .section-block {
      &.mobile-full {
        padding: 0 24px;
      }
    }
  }
`;

export const PreviewDailyNewsMain = styled.div`
  background-color: ${colorsV2.white100};

  .section-container {
    max-width: 528px;

    ${fromScreen(1024)} {
      max-width: 1080px;
    }
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
  .section-block {
    display: grid;
    gap: 24px;

    &.mobile-full {
      padding: 0;
    }
  }

  .grid-row {
    display: grid;
    gap: 24px;
  }

  .hidden {
    display: none;
  }

  #left-interaction-sidebar {
    width: 160px;
    min-width: 160px;
    max-width: 160px;
  }

  @media ${deviceBreakpoints.fromTablet} {
    .section-block {
      &.mobile-full {
        padding: 0 24px;
      }
    }
  }
`;

export const stepTrackingActions = ['validateGoToStep', 'validateNextStep', 'prevStep'];

export interface SaveApiPayload {
  id: string;
  payload: any;
  onSuccess?: any;
}

interface UseDataEntryStepActionParams {
  getDetailData: () => any;
  getSaveData: () => any;
  saveApi: ({ id, payload, onSuccess }: SaveApiPayload) => void;
}

export const useDataEntryStepAction = ({
  getDetailData,
  getSaveData,
  saveApi,
}: UseDataEntryStepActionParams) => {
  const stepStore = useStepStore();

  const saveStep = () => {
    saveApi({
      id: getDetailData()?.id,
      payload: {
        ...getDetailData(),
        ...getSaveData(),
      },
    });
  };

  useInterval(() => {
    saveStep();
  }, 30000);

  React.useEffect(() => {
    const disposer = onAction(stepStore, async (call) => {
      if (stepTrackingActions.includes(call.name)) {
        const onSuccess = () => {
          switch (call.name) {
            case 'validateNextStep': {
              stepStore.nextStep();
              break;
            }
            case 'validateGoToStep': {
              const stepIndex = call.args[0];
              stepStore.forceGoToStep(stepIndex);
              break;
            }
            default:
          }
        };
        saveApi({
          id: getDetailData().id,
          payload: {
            ...getDetailData(),
            ...getSaveData(),
          },
          onSuccess,
        });
      }
    });
    return () => {
      disposer();
    };
  }, [stepStore, getDetailData, saveApi, getSaveData]);

  return {
    saveStep,
  };
};

export const FIELD_NAME_YEAR_QUARTER = 'year-quarter';

export const CONTENT_GROUP_OPTIONS = [
  { displayName: 'IELTS Writing Sample', value: 'WRITING' },
  { displayName: 'IELTS Speaking Sample', value: 'SPEAKING' },
];

export const WRITING_SAMPLE_TYPE_DROPDOWN_OPTIONS = [
  {
    displayName: 'IELTS Writing Task 1 Sample',
    value: 'WRITING_TASK_1',
  },
  {
    displayName: 'IELTS Writing Academic Task 2 Sample',
    value: 'WRITING_TASK_2_ACADEMIC',
  },
  {
    displayName: 'IELTS Writing General Task 1 Sample',
    value: 'WRITING_TASK_2_GENERAL',
  },
];

export const SPEAKING_SAMPLE_TYPE_DROPDOWN_OPTIONS = [
  { displayName: 'IELTS Speaking Part 1', value: 'SPEAKING_PART_1' },
  { displayName: 'IELTS Speaking Part 2', value: 'SPEAKING_PART_2' },
  { displayName: 'IELTS Speaking Part 3', value: 'SPEAKING_PART_3' },
];

export const QUESTION_TYPE_OPTIONS = [
  { displayName: 'Agree & disagree', value: 'AGREE_AND_DISAGREE' },
  { displayName: 'Discuss both views', value: 'DISCUSS_BOTH_VIEWS' },
  { displayName: 'Two-part questions', value: 'TWO_PART_QUESTIONS' },
  {
    displayName: 'Problems-causes-solutions',
    value: 'PROBLEMS_CAUSES_SOLUTIONS',
  },
  {
    displayName: 'Advantages & Disadvantages',
    value: 'ADVANTAGES_AND_DISADVANTAGES',
  },
];

export const CHART_TYPE_OPTIONS = [
  { displayName: 'Bar Chart', value: 'BAR_CHART' },
  { displayName: 'Pie Chart', value: 'PIE_CHART' },
  { displayName: 'Table', value: 'TABLE' },
  { displayName: 'Line Graph', value: 'LINE' },
  { displayName: 'Mixed Charts', value: 'MIXED' },
  { displayName: 'Process', value: 'PROCESS' },
  { displayName: 'Map', value: 'MAP' },
];

export const DOCUMENT_TYPE_OPTIONS = [
  { displayName: 'Thanks Letter', value: 'THANK_LETTER' },
  { displayName: 'Request Letter', value: 'REQUEST_LETTER' },
  { displayName: 'Complaint Letter', value: 'COMPLAINT_LETTER' },
  { displayName: 'Apology Letter', value: 'APOLOGY_LETTER' },
  {
    displayName: 'Advice-seeking Letter',
    value: 'ADVICE_SEEKING_LETTER',
  },
  { displayName: 'Application Letter', value: 'APPLICATION_LETTER' },
];

export const WRITING_SAMPLE_TYPE_OPTIONS = [
  { label: 'All Sample', value: null },
  { label: 'Task 1 General', value: 'WRITING_TASK_2_GENERAL' },
  { label: 'Task 1 Academic', value: 'WRITING_TASK_1' },
  { label: 'Task 2 Academic', value: 'WRITING_TASK_2_ACADEMIC' },
];

export const SPEAKING_SAMPLE_TYPE_OPTIONS = [
  { label: 'All Sample', value: null },
  { label: 'Part 1', value: 'SPEAKING_PART_1' },
  { label: 'Part 2', value: 'SPEAKING_PART_2' },
  { label: 'Part 3', value: 'SPEAKING_PART_3' },
];

export const WRITING_CHART_TYPE_OPTIONS = [
  { label: 'All Type', value: null },
  { label: 'Bar Chart', value: 'BAR_CHART' },
  { label: 'Pie Chart', value: 'PIE_CHART' },
  { label: 'Table', value: 'TABLE' },
  { label: 'Line Graph', value: 'LINE' },
  { label: 'Mixed Charts', value: 'MIXED' },
  { label: 'Process', value: 'PROCESS' },
  { label: 'Map', value: 'MAP' },
];

export const WRITING_QUESTION_TYPE_OPTIONS = [
  { label: 'All Type', value: null },
  { label: 'Agree & disagree', value: 'AGREE_AND_DISAGREE' },
  { label: 'Discuss both views', value: 'DISCUSS_BOTH_VIEWS' },
  { label: 'Two-part questions', value: 'TWO_PART_QUESTIONS' },
  {
    label: 'Problems-causes-solutions',
    value: 'PROBLEMS_CAUSES_SOLUTIONS',
  },
  {
    label: 'Advantages & Disadvantages',
    value: 'ADVANTAGES_AND_DISADVANTAGES',
  },
];

export const WRITING_DOCUMENT_TYPE_OPTIONS = [
  { label: 'All Type', value: null },
  { label: 'Thanks Letter', value: 'THANK_LETTER' },
  { label: 'Request Letter', value: 'REQUEST_LETTER' },
  { label: 'Complaint Letter', value: 'COMPLAINT_LETTER' },
  { label: 'Apology Letter', value: 'APOLOGY_LETTER' },
  { label: 'Advice-seeking Letter', value: 'ADVICE_SEEKING_LETTER' },
  { label: 'Application Letter', value: 'APPLICATION_LETTER' },
];

export const RESOURCE_STATUS_OPTIONS = [
  { label: 'All Status', value: null },
  { label: 'DRAFT', value: 'DRAFT' },
  { label: 'PUBLISHED', value: 'PUBLISHED' },
  { label: 'EDITING', value: 'EDITING' },
];

export const RESOURCE_STATUS_OPTIONS_2 = [
  { label: 'All', value: null },
  { label: 'DRAFT', value: 'DRAFT' },
  { label: 'PUBLISHED', value: 'PUBLISHED' },
  { label: 'EDITING', value: 'EDITING' },
];

export const LISTING_STATUS_OPTIONS = [
  { label: 'All Status', value: null },
  { label: 'Listed', value: 'LISTED' },
  { label: 'Unlisted', value: 'UNLISTED' },
  { label: 'Not listed yet', value: 'NOT_LISTED_YET' },
];

export const AVAILABLE_FOR_OPTIONS = [
  { label: 'ALL', value: null },
  { label: 'Nobody', value: 'NOBODY' },
  { label: 'Marketer', value: 'MARKETER' },
  { label: 'Academic', value: 'ACADEMIC' },
];

export const getInitialOutlineStatement = (detailData) => {
  if (detailData?.outline?.title) {
    return {
      ...detailData.outline.title,
      content: JSON.parse(detailData.outline.title.content),
    };
  }
  return {
    id: nanoid(),
    content: [outlineStatementTransforms.createOutlineStatementNode({ text: '' })],
  };
};

export const getInitialOutlineParagraph = (detailData) => {
  if (detailData?.outline?.contents) {
    return detailData.outline.contents.map((item) => {
      return {
        ...item,
        content: JSON.parse(item.content),
      };
    });
  }

  return [
    {
      id: nanoid(),
      content: [
        outlineParagraphTransforms.createOutlineParagraphNode({
          title: 'Body paragraph 1:',
        }),
      ],
    },
  ];
};

export const getInitialFeedbackData = (detailData) => {
  if (detailData.feedbacks) {
    return detailData.feedbacks.map((feedback) => {
      const { id, content } = feedback.feedbackContent;
      return {
        ...feedback,
        feedbackContent: {
          id,
          content: typeof content === 'string' ? JSON.parse(content) : content,
        },
      };
    });
  }
  return [
    {
      type: FeedbackTypeEnum.OVERALL,
      score: 5,
      feedbackContent: {
        id: nanoid(),
        content: [],
      },
    },
    {
      type: FeedbackTypeEnum.TASK_ACHIEVEMENT,
      score: 5,
      feedbackContent: {
        id: nanoid(),
        content: [feedbackTransforms.initialFeedbackValue()],
      },
    },
    {
      type: FeedbackTypeEnum.GRAMMAR,
      score: 5,
      feedbackContent: {
        id: nanoid(),
        content: [feedbackTransforms.initialFeedbackValue()],
      },
    },
    {
      type: FeedbackTypeEnum.VOCABULARY,
      score: 5,
      feedbackContent: {
        id: nanoid(),
        content: [feedbackTransforms.initialFeedbackValue()],
      },
    },
    {
      type: FeedbackTypeEnum.COHERENCE_AND_COHESION,
      score: 5,
      feedbackContent: {
        id: nanoid(),
        content: [feedbackTransforms.initialFeedbackValue()],
      },
    },
  ];
};

export const getInitialExerciseData = (detailData) => {
  if (detailData?.exercises) {
    return {
      ...detailData.exercises,
      content: JSON.parse(detailData.exercises.content),
    };
  }

  return {
    id: nanoid(),
    content: [getEmptyParagraphNode()],
  };
};

export const syncIdeaParagraphFromVocabFlow = (detailData, withTitleNode, isPreview = false) => {
  return [...detailData.samples].map((sampleNode) => {
    if (isPreview) {
      return {
        id: nanoid(),
        content: [
          withTitleNode ? generateVocabIdeaTitleNode() : undefined,
          getEmptyParagraphNode(),
        ].filter(Boolean),
      };
    }
    // don't take node type = vocab, instead get children text only
    let content = JSON.parse(sampleNode.content);
    content = content.map((n) => {
      n.children = n.children.map((item) => {
        if (item.type) {
          return { text: Node.string(item) };
        }
        return item;
      });
      return n;
    });
    return {
      id: nanoid(),
      content,
    };
  });
};

const getVocabIdeaContent = (content, withTitleNode) => {
  let contentResult = JSON.parse(content).filter((node) => {
    return !(!withTitleNode && node.type === ELEMENT_VOCAB_IDEA_TITLE);
  });

  if (withTitleNode && contentResult?.[0]?.type !== ELEMENT_VOCAB_IDEA_TITLE) {
    contentResult = [generateVocabIdeaTitleNode(), ...contentResult];
  }
  return contentResult;
};

export const getInitialIdeaFlow = (detailData, withTitleNode, isPreview = false) => {
  if (detailData?.ideas) {
    return detailData.ideas.map((idea) => {
      return {
        ...idea,
        content: getVocabIdeaContent(idea.content, withTitleNode),
      };
    });
  }

  if (detailData?.samples) {
    return syncIdeaParagraphFromVocabFlow(detailData, withTitleNode, isPreview);
  }

  return [
    {
      id: nanoid(),
      content: [
        withTitleNode ? generateVocabIdeaTitleNode() : undefined,
        getEmptyParagraphNode(),
      ].filter(Boolean),
    },
  ];
};

export const getInitialVocabData = (detailData, withTitleNode = false) => {
  if (detailData?.samples) {
    return detailData.samples.map((sample) => {
      const newContent = sample.content;
      return {
        ...sample,
        content: getVocabIdeaContent(newContent, withTitleNode),
      };
    });
  }
  return [
    {
      id: nanoid(),
      content: [
        withTitleNode ? generateVocabIdeaTitleNode() : undefined,
        getEmptyParagraphNode(),
      ].filter(Boolean),
    },
  ];
};

export const getInitialTranslationData = (detailData) => {
  if (detailData?.translations) {
    return detailData.translations.map((sample) => {
      return {
        ...sample,
        content: JSON.parse(sample.content),
      };
    });
  }
  if (detailData.samples) {
    return detailData.samples.map(() => ({
      id: nanoid(),
      content: getEmptyParagraphNode(),
    }));
  }
  return [
    {
      id: nanoid(),
      content: getEmptyParagraphNode(),
    },
  ];
};

export const getInitialQuestionListData = (detailData) => {
  if (detailData?.questions) {
    return {
      ...detailData.questions,
      content: JSON.parse(detailData.questions.content),
    };
  }

  return {
    id: nanoid(),
    content: [questionListTransforms.createQuestionListNode()],
  };
};

export const getTextFromIdeaVocabFlow = (detailData) => {
  let text = '';
  if (detailData?.samples) {
    detailData.samples.forEach((sample) => {
      const sampleContentNodes =
        typeof sample.content === 'string' ? JSON.parse(sample.content) : sample.content;
      text += `${sampleContentNodes.map((n) => Node.string(n)).join(' ')} `;
    });
  }

  return text;
};

export const getParagraphNodeText = getSlateNodeAsTextString;

export const getParagraphNodeTextWithoutTitle = (content) => {
  const contentWithoutTitle = content.filter((n) => n?.type !== ELEMENT_VOCAB_IDEA_TITLE);
  return getSlateNodeAsTextString(contentWithoutTitle);
};

export const syncVocabFlowFromQuestionList = (detailData, currentVocabs) => {
  const { questions } = detailData;
  if (questions?.content) {
    const { content } = questions;
    const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
    const questionListRawString = contentObj[0].children.map((node) => Node.string(node));

    return questionListRawString.map((titleString, index) => {
      const vocabParagraphs = (currentVocabs?.[index]?.content || [getEmptyParagraphNode()]).filter(
        (n) => n?.type !== ELEMENT_VOCAB_IDEA_TITLE
      );
      return {
        id: nanoid(),
        content: [generateVocabIdeaTitleNode(titleString), ...vocabParagraphs],
      };
    });
  }
  return [];
};

export const getSectionDescriptionByType = (detailData, type, isPreview = false) => {
  const foundSectionDescription = (detailData?.sectionDescriptions || []).find(
    (item) => item.sectionType === type
  );
  if (foundSectionDescription) {
    return {
      ...foundSectionDescription,
      content:
        typeof foundSectionDescription.content === 'string'
          ? JSON.parse(foundSectionDescription.content)
          : foundSectionDescription.content,
    };
  }

  if (isPreview) return null;

  return {
    sectionType: type,
    content: [getEmptyParagraphNode()],
  };
};

export const IeltsSampleHideableSectionEnum = {
  CONCLUSION: 'CONCLUSION',
  EXERCISE: 'EXERCISE',
  FEEDBACK: 'FEEDBACK',
} as const;

export type IeltsSampleHideableSectionEnum =
  (typeof IeltsSampleHideableSectionEnum)[keyof typeof IeltsSampleHideableSectionEnum];

export const getSectionConfigByType = (detailData, type) => {
  const foundSectionDescription = (detailData?.sectionConfigs || []).find(
    (item) => item.sectionType === type
  );
  return (
    foundSectionDescription || {
      sectionType: type,
      hide: false,
    }
  );
};

export const getSampleSectionHides = (detailData) => {
  return (detailData?.sectionConfigs || []).reduce((prevValue, currValue) => {
    return {
      ...prevValue,
      [currValue.sectionType]: Boolean(currValue?.hide),
    };
  }, {});
};
